import {Component, OnDestroy, OnInit, QueryList, ViewChildren} from '@angular/core';
import {UIChart} from 'primeng/chart';
import {GraphHandlerService} from '../bw-graph/graph-handler.service';
import {BaseClientComponent} from '../base-client.component';
import {AuthenticationService} from '../../authenticationModule/services/authentication.service';
import {CryptoService} from '../../services/crypto.service';
import {SelectItem} from 'primeng/api';

@Component({
  selector: 'app-time-sync',
  templateUrl: './crypto-monitoring.component.html',
  styleUrls: ['./crypto-monitoring.component.scss']
})
export class CryptoMonitoringComponent extends BaseClientComponent implements OnInit, OnDestroy {
  @ViewChildren('chart') charts !: QueryList<UIChart>;
  instances = [];
  selectedInstance = null;
  data = [];
  selectedPeriod = [];
  selectedUnit = [];
  isZoomedIn = [];
  labels = ['percentile_50', 'maximum', 'average', 'percentile_90', 'percentile_1'];
  labelsText = ['Median', 'Max', 'Avg', '90%', '1%'];
  borderColorsArray = ['#b62c38', '#FFA500', '#177430', '#5A002C', '#3366CC', '#F54927', '#ED6AFF'];
  graphOptions = [];
  instancesOptions: SelectItem[] = [];
  instanceDropdownWidth = '320px';
  cryptoData: any;
  graphs = [];
  loading = true;
  graphsTitle = [];
  periods = [
    {'value': 5, label: '5 min'},
    {'value': 60, label: '1 h'},
    {'value': 360, label: '6 h'},
    {'value': 1440, label: '1 day'},
    {'value': 2880, label: '2 days'},
    {'value': 5760, label: '4 days'},
    {'value': 10080, label: '7 days'}
  ];
  unitsByGraphId: Record<string, string> = {
    bridge_shaping_group_sender_counters: 'pkts/min',
    bridge_shaping_group_receiver_counters: 'pkts/min',
    bridge_packet_size_by_shaping_group: 'bytes',
    bridge_n_radio_fragments_by_shaping_group: 'fragments',
    bridge_shaping_latency_dist_by_shaping_group: 'µs', // CHANGED (was 'ms')
  };

  constructor(
    private cryptoService: CryptoService,
    private authenticationService: AuthenticationService,
    public graphHandlerService: GraphHandlerService) {
    super();
  }

  ngOnInit() {
    this.authenticationService.waitForAuthenticationThenLaunch(() => {
      this.cryptoService.getClientInstances().subscribe(
        res => {
          this.instances = res;
          this.instancesOptions = this.instances.map(instance => ({
            label: (this.authenticationService.getUserCurrent().permissions.isMckayUser ? instance.client + ' ' + instance.instance + ' ' : '')
              + instance.etreeShortName + ' VLAN ' + instance.vlan + ' (' + instance.etreeId + ')',
            value: instance.id
          })).sort((a, b) =>
            a.label.toLowerCase().localeCompare(b.label.toLowerCase())
          );
          this.instanceDropdownWidth = this.computeInstanceDropdownWidth();
        });
    });
  }

  private computeInstanceDropdownWidth(): string {
    const panel = document.createElement('div');
    panel.className = 'p-dropdown-panel';
    panel.style.visibility = 'hidden';
    panel.style.position = 'absolute';
    panel.style.whiteSpace = 'nowrap';
    const items = document.createElement('ul');
    items.className = 'p-dropdown-items';
    const item = document.createElement('li');
    item.className = 'p-dropdown-item';
    items.appendChild(item);
    panel.appendChild(items);
    document.body.appendChild(panel);

    let maxLabelWidth = 0;
    for (const option of this.instancesOptions) {
      item.textContent = option.label;
      maxLabelWidth = Math.max(maxLabelWidth, item.getBoundingClientRect().width);
    }
    document.body.removeChild(panel);

    return Math.max(maxLabelWidth + 60, 320) + 'px';
  }

  private buildGraphOptions(graphId: string) {
    const unit = this.unitsByGraphId[graphId] ?? '';
    const isShapingDelay = graphId === 'bridge_shaping_latency_dist_by_shaping_group';
    return {
      parsing: true,
      scales: {
        x: {
          type: 'time',
          min: this.graphHandlerService.minTime,
          time: {
            unit: 'hour',
            displayFormats: {day: 'MM-dd HH', hour: 'HH:mm'}
          },
          ticks: {callback: (value: any) => value},
          title: {display: true, text: 'Date'}
        },
        y: {
          min: 0,
          ticks: {
            callback: (value: any) => {
              const v = isShapingDelay ? Math.round(value) : value; //
              return unit ? `${v} ${unit}` : v;
            }
          }
        }
      },
      plugins: {
        zoom: {
          pan: {enabled: true, mode: 'x'},
          zoom: {wheel: {enabled: false}, mode: 'x'}
        }
      }
    };
  }

  changeSelectedInstance() {
    this.graphs = [];
    this.graphsTitle = [];
    this.cryptoData = null;
    if (this.selectedInstance) {
      this.loading = true;
      this.loadCryptoData();
    }
  }

  loadCryptoData() {
    this.cryptoService.getCryptoData(this.selectedInstance).subscribe(
      res => {
        this.cryptoData = res;
        // this.initGraph('bridge_shaping_group_sender_counters', 'Sent packets', res,
        //   ['fragments_dropped_before_radio', 'packets_sent_with_udp_compression', 'packets_sent_with_tcp_compression', 'packets_received_from_client'],
        //   ['Fragments Dropped Before Radio', 'Packets Sent With UDP Compression', 'Packets Sent With TCP Compression', 'Packets Received From Client']);
        // this.initGraph('bridge_shaping_group_receiver_counters', 'Received packets', res,
        //   ['gaps', 'fiber_packets_duplicated', 'fiber_gaps', 'fiber_packets_sent_to_client', 'radio_gaps', 'radio_packets_sent_to_client', 'packets_sent_to_client'],
        //   ['Gaps', 'Fiber Packets Duplicated', 'Fiber Gaps', 'Fiber Packets Sent To Client', 'Radio Gaps', 'Radio Packets Sent To Client', 'Packets Sent To Client']);
        this.initGraph('bridge_packet_size_by_shaping_group', 'Packet size', res, ['average'], ['Avg']);
        this.initGraph('bridge_n_radio_fragments_by_shaping_group', 'Fragments per packet', res, ['average'], ['Avg']);
        this.initGraph('bridge_shaping_latency_dist_by_shaping_group', 'Shaping delay', res);
        this.loading = false;
      }
    );
  }

  private initGraph(graphId, graphTitle, res, labels = [], labelsText = []) {
    this.graphsTitle.push(graphTitle);
    this.graphs.push(graphId);
    this.selectedPeriod[graphId] = 360;
    this.isZoomedIn[graphId] = false;
    this.selectedUnit[graphId] = 'hour';
    this.data[graphId] = {};
    this.data[graphId].labels = res.map(data => data.date * 1000);
    this.data[graphId].datasets = this.getGraphDataset(res, graphId, labels, labelsText);
    this.graphOptions[graphId] = this.buildGraphOptions(graphId);
  }

  getGraphDataset(res, graphId: string, labels, labelsText) {
    const dataArray = [];
    const isShapingDelay = graphId === 'bridge_shaping_latency_dist_by_shaping_group';
    if (labels.length === 0) {
      labels = this.labels;
    }
    if (labelsText.length === 0) {
      labelsText = this.labelsText;
    }
    for (let i = 0; i < labels.length; i++) {
      dataArray.push({
        type: 'line',
        label: labelsText[i],
        data: res.map(d => { // CHANGED
          const v = d[graphId]?.[0]?.[labels[i]];
          if (v == null) { return v; }
          return isShapingDelay ? Math.round(v / 1000) : v; // ns -> µs + no decimals
        }),
        id: graphId,
        borderColor: this.borderColorsArray[i],
        backgroundColor: this.borderColorsArray[i],
        tension: 0,
        fill: false,
        min: 0,
        pointHoverRadius: 5,
        pointRadius: 2,
        pointHoverBackgroundColor: '#3366CC'
      });
    }
    return dataArray;
  }

  resetCharZoom(graphId: string) {
    this.charts.map((chart: UIChart) => {
      if (chart.data && chart.data.datasets[0].id === graphId) {
        chart.options.scales.x.max = null;
        chart.options.scales.x.min = this.graphHandlerService.minTime;
        this.selectedPeriod[graphId] = 360;
        this.selectedUnit[graphId] = 'hour';
        chart.reinit();
      }
    });
  }

  updateTimeScale(graphId: string) {
    this.charts.map((chart: UIChart) => {
      if (chart.data && chart.data.datasets[0].id === graphId) {
        const endDate = new Date();
        const maxTime = endDate.getTime();
        endDate.setMinutes(endDate.getMinutes() - this.selectedPeriod[graphId]);
        chart.options.scales.x.min = endDate.getTime();
        chart.options.scales.x.max = maxTime;
        chart.options.scales.x.time.unit = this.selectedUnit[graphId];
        chart.reinit();
      }
    });
  }

  zoomIn(graph: string) {
    this.isZoomedIn[graph] = true;
  }

  zoomOut(graph: string) {
    this.isZoomedIn[graph] = false;
  }
}
